import type { AppNotification, CalendarWorkout, CalendarWorkoutType } from '../../services/api';
import { isNotificationUnread } from '../../utils/countUnreadNotifications';

export function isWorkoutNotification(
  item: AppNotification,
): item is AppNotification & { notifiable: Record<string, unknown> & { id: number } } {
  return item.notifiable_type === 'Workout' && typeof item.notifiable === 'object' && item.notifiable != null && 'id' in item.notifiable;
}

export function isEventNotification(
  item: AppNotification,
): item is AppNotification & { notifiable: { id: number; event_date?: string; name?: string } } {
  return item.notifiable_type === 'Event' && typeof item.notifiable === 'object' && item.notifiable != null && 'id' in item.notifiable;
}

export function isMetricNotification(
  item: AppNotification,
): item is AppNotification & {
  notifiable: { unique_code?: string; date?: string; metric_type_name?: string; value?: string | number };
} {
  return (
    item.notifiable_type === 'UserMetricData' &&
    typeof item.notifiable === 'object' &&
    item.notifiable != null
  );
}

export function notificationWorkoutToCalendar(notifiable: Record<string, unknown>): CalendarWorkout {
  const workoutType = (notifiable.workout_type ?? {}) as Partial<CalendarWorkoutType>;
  return {
    id: Number(notifiable.id),
    workout_type: {
      id: Number(workoutType.id ?? 0),
      name: String(workoutType.name ?? 'Workout'),
      english_name: String(workoutType.english_name ?? workoutType.name ?? 'Workout'),
      icon: String(workoutType.icon ?? ''),
    },
    title: String(notifiable.title ?? ''),
    workout_day: String(notifiable.workout_day ?? ''),
    total_time: notifiable.total_time != null ? Number(notifiable.total_time) : null,
    total_time_planned:
      notifiable.total_time_planned != null ? String(notifiable.total_time_planned) : null,
    tss_actual: notifiable.tss_actual != null ? Number(notifiable.tss_actual) : null,
    tss_planned: notifiable.tss_planned != null ? String(notifiable.tss_planned) : null,
    if_planned: notifiable.if_planned != null ? String(notifiable.if_planned) : null,
    description: notifiable.description != null ? String(notifiable.description) : null,
    distance: notifiable.distance != null ? Number(notifiable.distance) : null,
    distance_planned:
      notifiable.distance_planned != null ? String(notifiable.distance_planned) : null,
    calories: notifiable.calories != null ? String(notifiable.calories) : null,
    order_on_day: notifiable.order_on_day != null ? Number(notifiable.order_on_day) : null,
    is_created_by_athlete: Boolean(notifiable.is_created_by_athlete),
    structure: notifiable.structure ?? null,
    comments: Number(notifiable.comments ?? 0),
  };
}

export function markNotificationReadInGroups(
  groups: Record<string, AppNotification[]>,
  notificationId: number,
): Record<string, AppNotification[]> {
  const next: Record<string, AppNotification[]> = {};
  for (const [key, items] of Object.entries(groups)) {
    next[key] = items.map((item) =>
      item.id === notificationId ? { ...item, is_read: true } : item,
    );
  }
  return next;
}

export function wasUnread(item: AppNotification): boolean {
  return isNotificationUnread(item.is_read);
}

/** YYYY-MM-DD from API date string for calendar form deep links. */
export function toGregorianDateParam(value: string | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
